import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import { ICommission } from "../../../../types/commission.type";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { updateCommissionConsultant } from "@/common/services/commission.api";
import { Modal } from "flowbite-react/components/Modal";
import { Button, Spinner } from "flowbite-react";
import { useConsultants } from "./useConsultants";

interface ModalProps {
  commission: ICommission;
  onClose: () => void;
  setActualize: (value: boolean) => void;
  actualize: boolean;
}

const validationSchema = Yup.object().shape({
  newTechnicalConsultant: Yup.string().required("Selecione um novo consultor."),
});

interface SelectOption {
  label: string;
  value: string;
}

interface CustomSelectProps {
  label: string;
  options: SelectOption[];
  placeholder?: string;
  name: string;
  disabled: boolean;
}

const FormikSelect: React.FC<CustomSelectProps> = ({
  label,
  options,
  disabled,
  ...props
}) => {
  const [field, meta] = useField(props.name);
  const isError = meta.touched && meta.error;

  return (
    <div className="flex flex-col space-y-1">
      <label htmlFor={field.name} className="text-sm font-medium text-gray-700">
        {label}
      </label>
      <select
        id={field.name}
        {...field}
        {...props}
        disabled={disabled}
        className={`
                    w-full text-sm p-2 border rounded-lg 
                    ${
                      isError
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-blue-500"
                    } 
                    focus:border-blue-500 transition-colors
                `}
      >
        {options.map((option) => (
          <option key={option.value || "default"} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {isError && <div className="text-xs text-red-500 mt-1">{meta.error}</div>}
    </div>
  );
};

const ModalChangeConsultant: React.FC<ModalProps> = ({
  commission,
  onClose,
  setActualize,
  actualize,
}) => {
  const { notifySuccess, notifyError } = useToastContext();
  const initialConsultant = commission.technicalConsultant || "";

  const { options: consultantsList, isLoading: isLoadingConsultants } =
    useConsultants();

  const handleSubmit = async (
    values: { newTechnicalConsultant: string },
    { setSubmitting }
  ) => {
    if (!values.newTechnicalConsultant) {
      notifyError("Por favor, selecione um consultor válido.");
      setSubmitting(false);
      return;
    }

    try {
      const result = await updateCommissionConsultant(
        commission._id!,
        values.newTechnicalConsultant
      );

      if (result.ok) {
        notifySuccess(
          `Comissão de ${commission.renterName} transferida para ${values.newTechnicalConsultant} com sucesso.`
        );
        setActualize(!actualize);
        onClose();
      } else {
        notifyError(result.message || "Falha ao transferir consultor.");
      }
    } catch (error) {
      notifyError("Erro inesperado ao salvar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={true} onClose={onClose}>
      <Modal.Header>
        <h2 className="text-black">{`Trocar Consultor Técnico para ${commission.renterName}`}</h2>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={{ newTechnicalConsultant: initialConsultant }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                Consultor Atual: <strong>{initialConsultant}</strong>
              </p>

              <FormikSelect
                name="newTechnicalConsultant"
                label="Novo Consultor Técnico"
                options={consultantsList}
                disabled={isLoadingConsultants || isSubmitting}
              />

              {isLoadingConsultants && (
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Spinner size="sm" /> Carregando consultores...
                </p>
              )}

              <div className="flex justify-end gap-2 mt-4">
                <Button
                  type="button"
                  color="light"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  disabled={isSubmitting || isLoadingConsultants}
                  className="bg-red"
                  color={"green"}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span>Confirmando...</span>
                    </div>
                  ) : (
                    "Confirmar Troca"
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default ModalChangeConsultant;
